import { useCallback, useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { echo, useConnectionStatus } from '@laravel/echo-react';
import type { ExportProgressUpdatedPayload } from '@/hooks/websocket/types';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { TERMINAL_STATUSES, exportStatusSchema } from '@/types/export';
import { getExport, getShareExport } from '@/services/export.service';
import { useExportStore } from '@/hooks/data/export/useExportStore';
import { QueryKey } from '@/lib/queryKeys';

// Caps the poll floor so a stuck export backs off instead of polling fast forever.
const MAX_POLL_INTERVAL_MS = 30_000;
const MAX_POLL_ATTEMPTS = 100;

interface UseExportChannelOptions {
  enabled?: boolean;
  // Routes reconciliation to the share-scoped endpoint. Polling below runs for
  // DAM exports too, not only share ones.
  shareId?: string;
}

export const useExportChannel = (exportId: string | undefined, options: UseExportChannelOptions = {}): void => {
  const { enabled = true, shareId } = options;
  const updateExport = useExportStore((state) => state.updateExport);
  const getExportFn = useAuthenticatedQueryFn(getExport);
  const getShareExportFn = useAuthenticatedQueryFn(getShareExport);
  const queryClient = useQueryClient();
  const connectionStatus = useConnectionStatus();
  const wasDisconnected = useRef(false);

  // The WS channel is the fast path but its broadcast can be dropped or lost to an
  // unmount, so poll as the floor until the export reaches a terminal status.
  const { data: snapshot } = useQuery({
    queryKey: shareId ? [QueryKey.ShareExport, shareId, exportId] : [QueryKey.Export, exportId],
    queryFn: () =>
      shareId ? getShareExportFn({ shareId, id: exportId as string }) : getExportFn({ id: exportId as string }),
    enabled: enabled && Boolean(exportId),
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && TERMINAL_STATUSES.includes(status)) {
        return false;
      }
      const pollsSoFar = Math.max(0, query.state.dataUpdateCount - 1);
      if (pollsSoFar >= MAX_POLL_ATTEMPTS) {
        return false;
      }
      return Math.min(2500 * 1.5 ** pollsSoFar, MAX_POLL_INTERVAL_MS);
    },
  });

  useEffect(() => {
    if (!snapshot) {
      return;
    }
    // Never let a stale snapshot roll back fresher WS events: only apply it when
    // it advances progress or carries a terminal status.
    const current = useExportStore.getState().exports.find((item) => item.id === snapshot.id);
    const advances = !current || snapshot.progressPercent > current.progress;
    if (advances || TERMINAL_STATUSES.includes(snapshot.status)) {
      updateExport(snapshot.id, { progress: snapshot.progressPercent, status: snapshot.status });
    }
  }, [snapshot, updateExport]);

  const handleProgressUpdated = useCallback(
    (event: ExportProgressUpdatedPayload) => {
      const parsedStatus = exportStatusSchema.safeParse(event.status);
      // A non-terminal fallback keeps the bar in progress if the backend ever sends
      // an unrecognised status, rather than dropping the percentage update.
      const status = parsedStatus.success ? parsedStatus.data : 'processing';
      // Guard the raw WS number the same way the status is guarded: a malformed
      // payload must not push NaN/undefined into the bar width.
      if (!Number.isFinite(event.progressPercent)) {
        return;
      }
      updateExport(event.exportId, { progress: Math.min(100, Math.max(0, event.progressPercent)), status });
    },
    [updateExport],
  );

  useEffect(() => {
    if (!exportId || !enabled) {
      return;
    }

    const channelName = `export.${exportId}`;
    const channel = echo().private(channelName);
    channel.listen('.export.progress.updated', handleProgressUpdated);

    return () => {
      channel.stopListening('.export.progress.updated', handleProgressUpdated);
      echo().leaveChannel(`private-${channelName}`);
    };
  }, [exportId, enabled, handleProgressUpdated]);

  // Reconnect healing: if the WS drops while an export is in flight, the completion event
  // is never received. On reconnect, invalidate the snapshot query so it re-fetches the
  // current state and the bar heals to the terminal status.
  useEffect(() => {
    wasDisconnected.current = false;
  }, [exportId]);

  useEffect(() => {
    if (!exportId || !enabled) {
      return;
    }
    if (connectionStatus === 'disconnected' || connectionStatus === 'failed') {
      wasDisconnected.current = true;
    } else if (connectionStatus === 'connected' && wasDisconnected.current) {
      wasDisconnected.current = false;
      void queryClient.invalidateQueries({
        queryKey: shareId ? [QueryKey.ShareExport, shareId, exportId] : [QueryKey.Export, exportId],
      });
    }
  }, [connectionStatus, exportId, enabled, queryClient, shareId]);
};
